import { cn } from '@/lib/utils';

/**
 * Small colour-coded status pill — the controlled vocabulary for any
 * lifecycle state (entity / user / future policy / claim) across CORTEX.
 *
 * Colours come from the shadcn semantic tokens (`success` / `warning` /
 * `info` / `muted`) defined in `apps/web/index.css`, not hard-coded
 * Tailwind palette classes, so a state looks identical everywhere and
 * follows the theme (including the future dark mode).
 *
 * Lives in `components/ui` because it is product-agnostic — any tab that
 * surfaces an `Active | Suspended | Pending` state imports this instead
 * of rolling its own.
 */

export type StatusBadgeTone =
  | 'active'
  | 'suspended'
  | 'pending'
  | 'info'
  | 'warning'
  | 'danger'
  | 'neutral';

interface StatusBadgeProps {
  readonly tone: StatusBadgeTone;
  readonly label: string;
}

const TONE_CLASSES: Readonly<Record<StatusBadgeTone, string>> = {
  active: 'bg-success/10 text-success border-success/20',
  suspended: 'bg-muted text-muted-foreground border-border',
  pending: 'bg-warning/10 text-warning border-warning/20',
  info: 'bg-info/10 text-info border-info/20',
  warning: 'bg-warning/10 text-warning border-warning/20',
  danger: 'bg-destructive/10 text-destructive border-destructive/20',
  neutral: 'bg-muted text-muted-foreground border-border',
};

const TONE_DOTS: Readonly<Record<StatusBadgeTone, string>> = {
  active: 'bg-success',
  suspended: 'bg-muted-foreground',
  pending: 'bg-warning',
  info: 'bg-info',
  warning: 'bg-warning',
  danger: 'bg-destructive',
  neutral: 'bg-muted-foreground',
};

export function StatusBadge({ tone, label }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-semibold uppercase tracking-wide',
        TONE_CLASSES[tone],
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', TONE_DOTS[tone])} aria-hidden="true" />
      {label}
    </span>
  );
}

export const toneForLifecycleStatus = (status: 'Active' | 'Suspended'): StatusBadgeTone =>
  status === 'Active' ? 'active' : 'suspended';
